<template>
    <el-drawer v-model="visible" title="SPU详情" direction="rtl" size="min(960px, 100vw)"
        append-to-body destroy-on-close custom-class="spu-detail-drawer" @close="invalidateRequest">
        <div v-loading="loading" class="spu-detail" element-loading-text="正在加载商品详情">
            <div v-if="error" class="state-panel">
                <el-empty :description="error" />
                <el-button type="primary" @click="loadDetail">重新加载</el-button>
            </div>
            <template v-else-if="detail">
                <section class="overview card">
                    <div class="gallery">
                        <el-carousel v-if="pictures.length" ref="carouselRef" height="280px" :autoplay="false" indicator-position="none" arrow="hover" @change="index => activePicture = index">
                            <el-carousel-item v-for="(url, index) in pictures" :key="`${index}-${url}`">
                                <el-image class="gallery-image" :src="url" fit="contain" :preview-src-list="pictures" :initial-index="index" preview-teleported>
                                    <template #error><div class="image-placeholder">图片加载失败</div></template>
                                </el-image>
                            </el-carousel-item>
                        </el-carousel>
                        <el-empty v-else description="暂无商品图片" :image-size="80" />
                        <div v-if="pictures.length > 1" class="gallery-thumbnails" aria-label="商品图片导航">
                            <button v-for="(url, index) in pictures" :key="index" type="button" :class="{ active: activePicture === index }"
                                :aria-label="`查看第${index + 1}张商品图片`" :aria-pressed="activePicture === index" @click="carouselRef?.setActiveItem(index)">
                                <img :src="url" alt="" loading="lazy" />
                            </button>
                        </div>
                        <div v-if="pictures.length" class="gallery-hint">{{ activePicture + 1 }} / {{ pictures.length }} · 点击主图查看大图</div>
                    </div>
                    <div class="overview-info">
                        <el-tag class="partner-tag" size="small">{{ text(goods.partnerName) }}</el-tag>
                        <h2>{{ text(goods.name) }}</h2>
                        <div class="muted">SPU ID：{{ text(goods.spuId) }}</div>
                        <div class="muted">品牌：{{ text(goods.brand) }}</div>
                        <div class="price-box">
                            <div class="price-item sale-price"><span>最低平台售价</span><strong>{{ price(goods.minPlatformSalePrice) }}</strong></div>
                            <div class="price-item sale-price"><span>最高平台售价</span><strong>{{ price(goods.maxPlatformSalePrice) }}</strong></div>
                            <div class="price-item"><span>最低采购价</span><b>{{ price(goods.minPurchasePrice) }}</b></div>
                            <div class="price-item"><span>最高采购价</span><b>{{ price(goods.maxPurchasePrice) }}</b></div>
                        </div>
                    </div>
                </section>

                <section class="card">
                    <h3>类目信息</h3>
                    <el-descriptions :column="1" border>
                        <el-descriptions-item label="平台类目">{{ categoryPath([goods.platformFirstLevelCategoryName, goods.platformSecondLevelCategoryName]) }}</el-descriptions-item>
                        <el-descriptions-item label="合作方类目">{{ categoryPath([goods.partnerFirstLevelCategoryName, goods.partnerSecondLevelCategoryName, goods.partnerThirdLevelCategoryName]) }}</el-descriptions-item>
                    </el-descriptions>
                </section>

                <section class="card">
                    <h3>商品规格</h3>
                    <div v-for="(group, index) in specGroups" :key="group.specId ?? index" class="spec-group">
                        <div class="spec-name">{{ text(group.specName, '规格') }}<small>{{ group.values?.length || 0 }} 项</small></div>
                        <div class="spec-values">
                            <div v-for="(item, itemIndex) in group.values" :key="item.specValueId ?? itemIndex" class="spec-item">
                                <el-image v-if="item.picUrl" class="spec-image" :src="item.picUrl" fit="contain" :preview-src-list="[item.picUrl]" preview-teleported>
                                    <template #error><div class="image-placeholder">无图</div></template>
                                </el-image>
                                <span>{{ text(item.value) }}</span>
                            </div>
                            <span v-if="!group.values || !group.values.length" class="muted">暂无规格值</span>
                        </div>
                    </div>
                    <el-empty v-if="!specGroups.length" description="暂无商品规格" :image-size="60" />
                </section>

                <section class="card">
                    <h3>配送覆盖区</h3>
                    <el-descriptions :column="1" border>
                        <el-descriptions-item label="配送状态"><el-tag :type="deliveryTagType">{{ deliveryStatus }}</el-tag></el-descriptions-item>
                        <el-descriptions-item label="运费描述">{{ text(detail.freeShippingModel) }}</el-descriptions-item>
                        <el-descriptions-item v-if="districts.length" label="地区列表">
                            <ul class="districts"><li v-for="(district, index) in districts" :key="index" class="district-item">{{ district }}</li></ul>
                        </el-descriptions-item>
                    </el-descriptions>
                </section>

                <section class="card">
                    <h3>商品详情</h3>
                    <div v-if="detailHtml" class="detail-html" v-html="detailHtml"></div>
                    <el-empty v-else description="暂无图文详情" :image-size="60" />
                </section>
            </template>
            <el-empty v-else-if="!loading" description="暂无SPU详情" />
        </div>
        <el-backtop target=".spu-detail-drawer .el-drawer__body" :visibility-height="300" :right="32" :bottom="32">
            <button class="back-to-top" type="button" aria-label="回到顶部" title="回到顶部">
                <el-icon><Top /></el-icon>
            </button>
        </el-backtop>
    </el-drawer>
</template>

<script setup>
import { computed, onBeforeUnmount, onDeactivated, ref } from 'vue';
import { queryGoodsDetail } from '../../../request/api';
import { Top } from '@element-plus/icons-vue';

const visible = ref(false);
const loading = ref(false);
const error = ref('');
const detail = ref(null);
const carouselRef = ref(null);
const activePicture = ref(0);
let params = null;
let requestId = 0;
const isEmpty = value => value === null || value === undefined || value === '';
const text = (value, fallback = '-') => isEmpty(value) ? fallback : value;
const goods = computed(() => detail.value?.goods || {});
const pictures = computed(() => Array.isArray(detail.value?.picUrls) ? detail.value.picUrls.filter(url => typeof url === 'string' && url.trim()) : []);
const specGroups = computed(() => Array.isArray(detail.value?.specGroups) ? detail.value.specGroups : []);
const categoryPath = values => values.filter(value => !isEmpty(value)).join(' / ') || '-';
const price = value => isEmpty(value) || !Number.isFinite(Number(value)) ? '-' : `¥${Number(value).toFixed(2)}`;
const deliveryStatus = computed(() => ({ 1: '不可配送', 2: '可配送', 3: '按地址判断' })[detail.value?.deliveryArea?.supportStatus] || '-');
const deliveryTagType = computed(() => ({ 1: 'danger', 2: 'success', 3: 'warning' })[detail.value?.deliveryArea?.supportStatus] || 'info');
// 按省、市、区、镇展示地区路径，仅有国家名称时显示国家。
const districts = computed(() => {
    const value = detail.value?.deliveryArea?.supportedDistricts;
    const items = Array.isArray(value) ? value : isEmpty(value) ? [] : [value];
    return items.filter(item => !isEmpty(item)).map(item => {
        if (typeof item !== 'object') return String(item);
        const names = [item.provinceName, item.cityName, item.districtName, item.townName]
            .filter(name => typeof name === 'string' && name.trim())
            .map(name => name.trim());
        return names.join(' / ') || item.fullName || item.areaName || item.name || item.countryName || '';
    }).filter(item => item.trim() && item !== '{}' && item !== '[]');
});

// 仅保留商品图文需要的静态标签和图片地址，避免接口 HTML 执行脚本或影响抽屉布局。
const detailHtml = computed(() => {
    if (!detail.value?.detailHtml) return '';
    const document = new DOMParser().parseFromString(String(detail.value.detailHtml), 'text/html');
    const allowed = new Set(['P', 'DIV', 'SPAN', 'BR', 'IMG', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'H4', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD', 'HR']);
    const clean = parent => Array.from(parent.children).forEach(node => {
        if (!allowed.has(node.tagName)) {
            node.remove();
            return;
        }
        const src = node.getAttribute('src');
        const alt = node.getAttribute('alt');
        Array.from(node.attributes).forEach(attribute => node.removeAttribute(attribute.name));
        if (node.tagName === 'IMG' && src && /^(https?:\/\/|\/\/)/i.test(src.trim())) {
            node.setAttribute('src', src.trim());
            node.setAttribute('alt', alt || '商品详情图片');
            node.setAttribute('loading', 'lazy');
        }
        clean(node);
    });
    clean(document.body);
    return document.body.innerHTML.trim();
});

const invalidateRequest = () => { requestId += 1; loading.value = false; };
const loadDetail = async () => {
    const currentId = ++requestId;
    loading.value = true;
    detail.value = null;
    activePicture.value = 0;
    error.value = '';
    try {
        const res = await queryGoodsDetail({ ...params });
        if (currentId !== requestId) return;
        if (res.data.code == 0) {
            detail.value = res.data.data?.goods ? res.data.data : null;
        } else {
            error.value = res.data.msg || 'SPU详情加载失败';
        }
    } catch (e) {
        if (currentId === requestId) error.value = 'SPU详情加载失败，请重试';
    } finally {
        if (currentId === requestId) loading.value = false;
    }
};
const open = query => {
    params = { partnerCode: query.partnerCode, spuId: query.spuId };
    visible.value = true;
    loadDetail();
};
onDeactivated(() => { visible.value = false; invalidateRequest(); });
onBeforeUnmount(invalidateRequest);
defineExpose({ open });
</script>

<style scoped lang="scss">
.spu-detail { min-height: 300px; color: #303133; padding: 0 20px 24px; }
.back-to-top { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; padding: 0; border: none; border-radius: 50%; background: #fff; color: #409eff; font-size: 22px; cursor: pointer; }
.back-to-top:hover { background: #ecf5ff; }
.back-to-top:focus-visible { outline: 2px solid #409eff; outline-offset: 2px; }
.card { border: 1px solid #ebeef5; border-radius: 4px; padding: 20px; margin-bottom: 16px; background: #fff; }
h3 { margin: 0 0 18px; padding-left: 10px; border-left: 3px solid #409eff; font-size: 16px; font-weight: 600; }
.overview { display: grid; grid-template-columns: 300px minmax(0, 1fr); gap: 24px; }
.gallery { min-width: 0; }
.gallery-image { width: 100%; height: 100%; }
.gallery-image :deep(.el-image__inner) { cursor: zoom-in; }
.gallery-thumbnails { display: flex; flex-wrap: nowrap; gap: 8px; overflow-x: auto; padding: 10px 0 6px; }
.gallery-thumbnails button { flex: 0 0 46px; width: 46px; height: 46px; padding: 3px; border: 1px solid #e4e7ed; border-radius: 4px; background: #fff; cursor: pointer; }
.gallery-thumbnails button.active { border-color: #409eff; box-shadow: inset 0 0 0 1px #409eff; }
.gallery-thumbnails button:focus-visible { outline: 2px solid #409eff; outline-offset: 2px; }
.gallery-thumbnails img { width: 100%; height: 100%; object-fit: contain; }
.gallery-hint { margin-top: 8px; color: #909399; font-size: 12px; text-align: center; }
.partner-tag { color: #ff0f23; background: #fff1f2; border-color: #ffcfd4; }
.image-placeholder { display: flex; align-items: center; justify-content: center; height: 100%; background: #f5f7fa; color: #909399; font-size: 12px; }
.overview-info { min-width: 0; }
h2 { margin: 14px 0; font-size: 19px; line-height: 1.6; overflow-wrap: anywhere; }
.muted { color: #909399; font-size: 14px; line-height: 1.9; overflow-wrap: anywhere; }
.price-box { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px 16px; margin-top: 20px; padding: 16px; background: #f5f7fa; border-radius: 4px; }
.price-item { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.price-box span { color: #909399; font-size: 12px; }
.price-box strong { color: #F56C6C; font-size: 21px; overflow-wrap: anywhere; }
.price-box b { color: #303133; font-size: 17px; font-weight: 500; overflow-wrap: anywhere; }
.spec-group { display: grid; grid-template-columns: 90px minmax(0, 1fr); gap: 16px; padding: 16px 0; }
.spec-group + .spec-group { border-top: 1px solid #ebeef5; }
.spec-name { padding-top: 8px; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
.spec-name small { display: block; margin-top: 6px; color: #909399; font-size: 12px; font-weight: 400; }
.spec-values { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 8px; align-items: start; }
.spec-item { display: flex; align-items: center; gap: 10px; min-height: 48px; padding: 8px 10px; background: #f7f8fa; border-radius: 4px; min-width: 0; box-sizing: border-box; font-size: 13px; line-height: 1.5; }
.spec-item span { overflow-wrap: anywhere; }
.spec-image { width: 32px; height: 32px; flex-shrink: 0; border-radius: 3px; background: #fff; cursor: zoom-in; }
.districts { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(160px, 100%), 1fr)); gap: 10px; margin: 0; padding: 6px 0; list-style: none; }
.district-item { display: flex; align-items: center; min-width: 0; min-height: 38px; box-sizing: border-box; padding: 9px 12px; border: 1px solid #ebeef5; border-radius: 4px; background: #f7f8fa; color: #606266; font-size: 13px; line-height: 1.6; overflow-wrap: anywhere; }
.detail-html { overflow-wrap: anywhere; line-height: 1.7; overflow-x: auto; }
.detail-html :deep(img) { display: block; max-width: 100%; height: auto; margin: 0 auto; }
.detail-html :deep(table) { border-collapse: collapse; max-width: 100%; }
.detail-html :deep(td), .detail-html :deep(th) { border: 1px solid #ebeef5; padding: 8px; }
.state-panel { text-align: center; padding: 30px 0; }
:deep(.el-descriptions__label) { width: 120px; }
:deep(.el-descriptions__content) { overflow-wrap: anywhere; }
@media (max-width: 680px) {
    .spu-detail { padding: 0 12px 16px; }
    .card { padding: 14px; }
    .overview { grid-template-columns: 1fr; gap: 16px; }
    .price-box strong { font-size: 19px; }
    .spec-group { grid-template-columns: 1fr; gap: 10px; }
    .spec-name { display: flex; align-items: baseline; gap: 8px; padding-top: 0; }
    .spec-name small { margin-top: 0; }
}
</style>
